import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, fmtMoney } from "@/lib/api";

/**
 * Iter 81 — BotPLDrilldown.
 *
 * Compact popover trigger that renders the passed ``label`` (usually
 * the top-contributor bot name). Clicking opens a panel listing the
 * bot's individual sell trades so users can see *which* trade paid,
 * not just the cumulative endpoint.
 *
 * Props:
 *   botId    : ObjectId string, or ``null`` for the "Manual" bucket
 *   botName  : display name shown in the popover header
 *   children : trigger content (usually a styled span)
 */
export default function BotPLDrilldown({ botId, botName, children }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  // Iter 82 — Drilldown Sort Toggle. Persist per-device so heavy
  // users don't have to re-click every time they open the panel.
  const [sortMode, setSortMode] = useState(() => {
    try {
      const v = localStorage.getItem("stratos.drilldown.sort");
      return v === "pl-desc" ? "pl-desc" : "chrono";
    } catch { return "chrono"; }
  });

  useEffect(() => {
    if (!open || data || loading) return;
    setLoading(true);
    setErr(null);
    const q = botId ? `bot_id=${encodeURIComponent(botId)}` : "bot_id=manual";
    api.get(`/portfolio/bot-sells?${q}&limit=20`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.message || "failed"))
      .finally(() => setLoading(false));
  }, [open, botId, data, loading]);

  const sortedTrades = React.useMemo(() => {
    const rows = (data?.trades || []).slice();
    if (sortMode === "pl-desc") {
      rows.sort((a, b) => (b.realized_pl ?? 0) - (a.realized_pl ?? 0));
    }
    // "chrono" is already newest-first from the backend.
    return rows;
  }, [data, sortMode]);
  // Iter 86 — Toast Dismiss Announcement. Screen-reader-only live
  // region that speaks "Sort preference reverted" when the user
  // clicks Undo on the sort-persist toast — otherwise AT users
  // have no confirmation the revert happened.
  const [undoAnnouncement, setUndoAnnouncement] = React.useState("");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="bot-pl-drilldown-trigger"
          className="cursor-pointer"
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="bot-pl-drilldown-panel"
        className="bg-zinc-950 border-zinc-800 w-[340px] p-0 mono text-xs"
        align="end"
      >
        {/* Iter 86 — Undo confirmation sr-only region. */}
        <div
          data-testid="bot-pl-drilldown-undo-aria-live"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {undoAnnouncement.split(" · ")[0]}
        </div>
        <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500">Bot P/L drilldown</div>
            <div className="text-zinc-200 font-semibold">{botName || "Bot"}</div>
          </div>
          {data && (
            <div
              data-testid="bot-pl-drilldown-total"
              className={`tabular-nums text-sm ${data.total_realized_pl >= 0 ? "text-amber-300" : "text-red-300"}`}
              title="Total realized P/L across the bot's sell trades"
            >
              {data.total_realized_pl >= 0 ? "+" : ""}{fmtMoney(data.total_realized_pl)}
            </div>
          )}
        </div>
        {/* Iter 82 — sort toggle. */}
        {data && (data.trades || []).length > 1 && (
          <div className="px-3 py-1.5 border-b border-zinc-800/60 flex items-center gap-1">
            <span className="text-[9px] uppercase tracking-widest text-zinc-500">Sort</span>
            {[
              { key: "chrono", label: "Newest", arrow: "▲" },
              { key: "pl-desc", label: "Biggest wins", arrow: "▼" },
            ].map((opt) => {
              const active = sortMode === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  data-testid={`bot-pl-drilldown-sort-${opt.key}`}
                  data-active={active ? "true" : "false"}
                  onClick={() => {
                    const prev = sortMode;
                    if (prev === opt.key) return;
                    setSortMode(opt.key);
                    try { localStorage.setItem("stratos.drilldown.sort", opt.key); } catch {}
                    // Iter 84 — Sort Persist Toast. Confirm the choice
                    // + surface the fact that it's remembered per
                    // device via an Undo action that flips back.
                    // Iter 85 — Toast Escape Key. Register a
                    // document-level keydown listener that dismisses
                    // the toast on Escape so keyboard users don't
                    // have to Tab into the Sonner region. Torn down
                    // on toast dismissal (onDismiss/onAutoClose).
                    let toastId;
                    const onEsc = (e) => {
                      if (e.key === "Escape") {
                        e.preventDefault();
                        toast.dismiss(toastId);
                      }
                    };
                    const cleanup = () => {
                      document.removeEventListener("keydown", onEsc, true);
                    };
                    document.addEventListener("keydown", onEsc, true);
                    toastId = toast(`Sorting by ${opt.label.toLowerCase()}`, {
                      description: "Preference saved for this device.",
                      duration: 4500,
                      action: {
                        label: "Undo",
                        onClick: () => {
                          setSortMode(prev);
                          try { localStorage.setItem("stratos.drilldown.sort", prev); } catch {}
                          // Iter 86 — sr-only confirmation. Timestamp
                          // in the value forces re-announcement even
                          // when the prev label matches a previous
                          // undo revert.
                          const prevLabel = prev === "pl-desc" ? "biggest wins" : "newest";
                          setUndoAnnouncement(`Sort preference reverted to ${prevLabel} · ${Date.now()}`);
                        },
                      },
                      onDismiss: cleanup,
                      onAutoClose: cleanup,
                    });
                  }}
                  className={`mono text-[9px] uppercase tracking-widest px-1.5 py-[1px] rounded-sm border transition-colors inline-flex items-center gap-1 ${
                    active
                      ? "border-amber-500 bg-amber-500/10 text-amber-300"
                      : "border-zinc-800 text-zinc-500 hover:text-zinc-200 hover:border-zinc-600"
                  }`}
                  title={opt.key === "chrono" ? "Newest trades first" : "Biggest realized P/L first"}
                  aria-pressed={active}
                >
                  {opt.label}
                  {/* Iter 83 — Sort Direction Arrow. Shown only on the
                      active button so keyboard-only users see the
                      effective ordering without eyeballing rows. */}
                  {active && (
                    <span
                      data-testid={`bot-pl-drilldown-sort-arrow-${opt.key}`}
                      aria-hidden="true"
                      className="text-[8px] tabular-nums"
                    >
                      {opt.arrow}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        <div className="max-h-[260px] overflow-y-auto divide-y divide-zinc-800/60">
          {loading && (
            <div className="px-3 py-4 text-[11px] text-zinc-500" data-testid="bot-pl-drilldown-loading">
              Loading trades…
            </div>
          )}
          {err && !loading && (
            <div className="px-3 py-4 text-[11px] text-red-400" data-testid="bot-pl-drilldown-error">
              Failed to load: {err}
            </div>
          )}
          {!loading && !err && data && (data.trades || []).length === 0 && (
            <div className="px-3 py-4 text-[11px] text-zinc-500" data-testid="bot-pl-drilldown-empty">
              No sell trades on record for this bot yet.
            </div>
          )}
          {!loading && !err && data && sortedTrades.map((t) => {
            const when = (t.executed_at || "").slice(0, 10);
            const pl = t.realized_pl ?? 0;
            return (
              <div
                key={t.id}
                data-testid={`bot-pl-drilldown-row-${t.id}`}
                className="px-3 py-2 flex items-center gap-3"
              >
                <div className="w-16 shrink-0 text-[10px] text-zinc-500 tabular-nums">{when}</div>
                <div className="flex-1 flex items-center gap-2 min-w-0">
                  <span className="font-semibold text-zinc-200">{t.symbol}</span>
                  <span className="text-[10px] text-zinc-500 tabular-nums">
                    {t.quantity}@{fmtMoney(t.price)}
                  </span>
                </div>
                <div
                  className={`tabular-nums ${pl >= 0 ? "text-amber-300" : "text-red-300"}`}
                  title={`Realized P/L on this trade: ${pl >= 0 ? "+" : ""}${fmtMoney(pl)}`}
                >
                  {pl >= 0 ? "+" : ""}{fmtMoney(pl)}
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
